import turfBbox from '@turf/bbox';
import turfBuffer from '@turf/buffer';
import turfCircle from '@turf/circle';
import { Feature, MultiPolygon, Polygon, Units } from '@turf/helpers';
import GeoPoint from 'geopoint';
import { DateTime } from 'luxon';

import DistanceUnit from '@/common/enums/DistanceUnit.enum';
import GeofenceDynamicGeofences from '@/common/models/geo/GeofenceDynamicGeofences';
import { getStore, GoogleMapsApiKey } from '@/globals';
import * as types from '@/store/modules/models/mutation-types';
import Validatable from '@/validator/Validatable';

const DefaultRadius = 0.01;
const DefaultExtrusion = 3.03;
const DefaultRadiusInUnit = 0.01;
const DefaultRadiusUnit = DistanceUnit.METER;

interface IGeofenceAttribute {
  group: string;
  values: [{ id: number; title: string }];
  selections; // []
}

export interface IGeofenceAPI {
  geofence_id: number;
  geofencegroup_id: number;
  name: string;
  description: string;
  tags: string;
  lat: number;
  lng: number;
  bounds_sw_lat: number;
  bounds_sw_lng: number;
  bounds_ne_lat: number;
  bounds_ne_lng: number;
  geojson: Polygon | MultiPolygon;
  dynamic_options: number;
  dynamic_contour: string;
  dynamic_profile: string;
}

export interface IGeofenceShortenedAPI {
  id?: number;
  ggid: number;
  n: string;
  d: string;
  tg: string;
  lat: number;
  lng: number;
  swt: number;
  swg: number;
  net: number;
  neg: number;
  g?: Polygon | MultiPolygon;
}

interface IGeofenceForPreviewScreens {
  geofence_id: number;
  geofencegroup_id: number;
  name: string;
}

export default class Geofence extends Validatable<Geofence> {
  geofenceGroupID: number;
  name: string;
  description: string;
  tags: string;
  lat?: number;
  lng?: number;
  boundsSwLat?: number;
  boundsSwLng?: number;
  boundsNeLat?: number;
  boundsNeLng?: number;
  geoJson?;
  placeSearchJson?;
  placeSearchProvider?: string;
  createdOnUtc?: string;
  modifiedOnUtc?: string;
  createdBy?: string;
  modifiedBy?: string;
  isSelected: boolean;
  fromGeofences;
  mapboxDrawID?: string;
  timestamp: DateTime;
  properties: object;
  dynamicGeofences?: GeofenceDynamicGeofences;

  constructor(
    id: number = -1,
    geofenceGroupID = -1,
    name: string = null,
    description: string = null,
    tags = JSON.stringify({
      radius: DefaultRadius,
      extrusion: DefaultExtrusion,
      radiusUnit: DefaultRadiusUnit,
      radiusInUnit: DefaultRadiusInUnit,
    }),
    lat?: number,
    lng?: number,
    boundsSwLat?: number,
    boundsSwLng?: number,
    boundsNeLat?: number,
    boundsNeLng?: number,
    geoJson?,
    placeSearchJson?,
    placeSearchProvider?: string,
    createdOnUtc?: string,
    modifiedOnUtc?: string,
    createdByUserID?: string,
    modifiedByUserID?: string,
    mapboxDrawID?: string,
    properties: object = {},
    dynamicGeofences: GeofenceDynamicGeofences = null,
  ) {
    super();
    this.id = id;
    this.geofenceGroupID = geofenceGroupID;
    this.name = name;
    this.description = description;
    this.tags = tags;
    this.lat = lat;
    this.lng = lng;
    this.boundsSwLat = boundsSwLat;
    this.boundsSwLng = boundsSwLng;
    this.boundsNeLat = boundsNeLat;
    this.boundsNeLng = boundsNeLng;
    this.geoJson = geoJson;
    this.placeSearchJson = placeSearchJson;
    this.placeSearchProvider = placeSearchProvider;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.createdBy = createdByUserID;
    this.modifiedBy = modifiedByUserID;

    this.isSelected = false;
    this.fromGeofences = [];
    this.mapboxDrawID = mapboxDrawID;
    this.timestamp = DateTime.fromISO(modifiedOnUtc || '', { zone: 'utc' });
    this.properties = properties;

    this.dynamicGeofences = dynamicGeofences;
  }

  static fromObject(obj, emptyObject = new Geofence()) {
    if (emptyObject == null) {
      emptyObject = new Geofence(
        obj.id,
        obj.geofenceGroupID,
        obj.name,
        obj.description,
        obj.tags,
        obj.lat,
        obj.lng,
        obj.boundsSwLat,
        obj.boundsSwLng,
        obj.boundsNeLat,
        obj.boundsNeLng,
        obj.geoJson,
        obj.placeSearchJson,
        obj.placeSearchProvider,
        obj.createdOnUtc,
        obj.modifiedOnUtc,
        obj.createdBy,
        obj.modifiedBy,
        obj.mapboxDrawID,
        obj.properties,
        obj.dynamicGeofences,
      );
    }
    const geofence = super.fromObject(obj, emptyObject);
    geofence.updateBoundingBox();
    return geofence;
  }

  get isNew(): boolean {
    return this.createdOnUtc == null;
  }

  get boundingBox(): [number, number, number, number] {
    return [this.boundsSwLng, this.boundsSwLat, this.boundsNeLng, this.boundsNeLat];
  }

  get geometry(): Polygon | MultiPolygon {
    if (this.isPoint && this.radius) {
      const units: Units = 'kilometers';
      const options = {
        steps: 32,
        units,
      };

      const circle = turfCircle([this.lng, this.lat], this.radius, options);
      return circle.geometry;
    }
    const geometry = this.geoJson?.geometry != null ? this.geoJson.geometry : this.geoJson;
    if (typeof geometry === 'string') {
      return JSON.parse(geometry);
    }
    return geometry;
  }

  get feature(): Feature {
    return {
      type: 'Feature',
      geometry: this.geometry,
      id: this.id, // This is used by mapbox setFeatureState()
      properties: {
        id: this.id, // This is used by GeofenceListAndMap.activateGeofence()
        name: this.name,
        description: this.description,
        tags: JSON.stringify(this),
        extrusion: this.extrusion,
        radius: this.radius,
        isSelected: this.isSelected ? 'true' : 'false',
        ...this.properties,
      },
    };
  }

  get boundingBoxFeature(): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [this.boundsNeLng, this.boundsNeLat],
            [this.boundsSwLng, this.boundsNeLat],
            [this.boundsSwLng, this.boundsSwLat],
            [this.boundsNeLng, this.boundsSwLat],
            [this.boundsNeLng, this.boundsNeLat],
          ],
        ],
      },
      properties: {
        id: `${this.id}-bounds`,
      },
    };
  }

  get pointFeature(): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [this.lng, this.lat],
      },
      id: this.id,
      properties: {
        id: this.id,
        name: this.name,
        description: this.description,
        tags: JSON.stringify(this),
        radius: this.radius,
        extrusion: this.extrusion,
        isSelected: this.isSelected ? 'true' : 'false',
        ...this.properties,
      },
    };
  }

  get isPoint(): boolean {
    if (this.tags == null) {
      return this.geoJson == null || this.geoJson.type === 'Point';
    }
    const { isPoint } = JSON.parse(this.tags);
    return isPoint ?? true;
  }

  set isPoint(val: boolean) {
    const tags = this.tags != null ? JSON.parse(this.tags) : {};
    tags.isPoint = val;
    this.tags = JSON.stringify(tags);
  }

  get radius(): number {
    if (this.tags == null) {
      return DefaultRadius;
    }
    const { radius } = JSON.parse(this.tags);
    return radius != null && !Number.isNaN(radius) ? radius : DefaultRadius;
  }

  set radius(val: number) {
    const tags = this.tags != null ? JSON.parse(this.tags) : {};
    tags.radius = val;
    this.tags = JSON.stringify(tags);

    if (val) {
      [this.boundsSwLng, this.boundsSwLat, this.boundsNeLng, this.boundsNeLat] = turfBbox(this.geometry);
    } else {
      [this.boundsSwLng, this.boundsSwLat, this.boundsNeLng, this.boundsNeLat] = [
        this.lng,
        this.lat,
        this.lng,
        this.lat,
      ];
    }
  }

  get radiusUnit(): { radiusUnit: string } {
    if (!this.tags) {
      return null;
    }

    const { radiusUnit = null } = JSON.parse(this.tags);
    return radiusUnit;
  }

  set radiusUnit(radiusUnit: string) {
    const tags = this.tags ? JSON.parse(this.tags) : {};
    tags.radiusUnit = radiusUnit;
    this.tags = JSON.stringify(tags);
  }

  get radiusInUnit(): { radiusInUnit: number } {
    if (!this.tags) {
      return null;
    }

    const { radiusInUnit = this.radius || null } = JSON.parse(this.tags);
    return radiusInUnit;
  }

  set radiusInUnit(radiusInUnit: number) {
    const tags = this.tags ? JSON.parse(this.tags) : {};
    tags.radiusInUnit = radiusInUnit;
    this.tags = JSON.stringify(tags);
  }

  get logo(): string {
    if (this.tags == null) {
      return null;
    }
    const { logo } = JSON.parse(this.tags);
    return logo ?? null;
  }

  set logo(val: string) {
    const tags = this.tags != null ? JSON.parse(this.logo) : {};
    tags.logo = val;
    this.tags = JSON.stringify(tags);
  }

  get startDate(): DateTime {
    if (this.tags == null) {
      return null;
    }
    const { startDate } = JSON.parse(this.tags);
    return startDate != null ? DateTime.fromISO(startDate) : null;
  }

  set startDate(date: DateTime) {
    const tags = this.tags != null ? JSON.parse(this.tags) : {};
    if (date != null) {
      tags.startDate = date.toISODate();
    } else {
      delete tags.startDate;
    }
    this.tags = JSON.stringify(tags);
  }

  get endDate(): DateTime {
    if (this.tags == null) {
      return null;
    }
    const { endDate } = JSON.parse(this.tags);
    return endDate != null ? DateTime.fromISO(endDate) : null;
  }

  set endDate(date: DateTime) {
    const tags = this.tags != null ? JSON.parse(this.tags) : {};
    if (date != null) {
      tags.endDate = date.toISODate();
    } else {
      delete tags.endDate;
    }
    this.tags = JSON.stringify(tags);
  }

  get extrusion(): number {
    if (this.tags == null) {
      return DefaultExtrusion;
    }
    const { extrusion } = JSON.parse(this.tags);
    return extrusion != null && !Number.isNaN(extrusion) ? extrusion : DefaultExtrusion;
  }

  set extrusion(val: number) {
    const tags = this.tags != null ? JSON.parse(this.tags) : {};
    tags.extrusion = val;
    this.tags = JSON.stringify(tags);
  }

  get attributes(): IGeofenceAttribute[] {
    const attributes = [];
    if (this.placeSearchProvider === 'Here') {
      const hereApiObject = JSON.parse(this.placeSearchJson);
      if (hereApiObject.tags != null) {
        hereApiObject.tags.forEach((t) => {
          attributes.push({
            group: t.group,
            values: [{ id: t.id, title: t.title }],
            selections: [],
          });
        });
      }
      if (hereApiObject.category != null) {
        attributes.push({
          group: 'category',
          values: [{ id: hereApiObject.category.id, title: hereApiObject.category.title }],
          selections: [],
        });
      }
    }
    return attributes;
  }

  get displayName(): string {
    return this.description != null && this.description.trim() !== ''
      ? `${this.name} - ${this.description}`
      : this.name;
  }

  get polygonGoogleStaticImageUrl(): string {
    return this.getPolygonGoogleStaticImageUrl();
  }

  getPolygonGoogleStaticImageUrl(width = 640, height = 480): string {
    const size = { width, height };
    const markers = '';
    const coordinates = this.geometry.coordinates[0];
    const path = `fillcolor:0xFF008333%7Ccolor:0xFF0083%7Cweight:3%7C${coordinates
      .map((x) => `${x[1]},${x[0]}`)
      .join('%7C')}`;
    return `https://maps.googleapis.com/maps/api/staticmap?path=${path}${markers}&scale=2&size=${size.width}x${size.height}&maptype=satellite&key=${GoogleMapsApiKey}`;
  }

  updateBoundingBox() {
    if (this.isPoint && this.radius) {
      // Use faster method when geofence is a point.
      const bb = new GeoPoint(this.lat, this.lng).boundingCoordinates(this.radius, null, true);
      getStore().commit(`${types.UPDATE_GEOFENCE_SWLAT}`, { geofence: this, value: bb[0].latitude() });
      getStore().commit(`${types.UPDATE_GEOFENCE_SWLNG}`, { geofence: this, value: bb[0].longitude() });
      getStore().commit(`${types.UPDATE_GEOFENCE_NELAT}`, { geofence: this, value: bb[1].latitude() });
      getStore().commit(`${types.UPDATE_GEOFENCE_NELNG}`, { geofence: this, value: bb[1].longitude() });
    } else if (this.geometry) {
      const bbox = turfBbox(this.geometry);
      getStore().commit(`${types.UPDATE_GEOFENCE_SWLAT}`, { geofence: this, value: bbox[1] });
      getStore().commit(`${types.UPDATE_GEOFENCE_SWLNG}`, { geofence: this, value: bbox[0] });
      getStore().commit(`${types.UPDATE_GEOFENCE_NELAT}`, { geofence: this, value: bbox[3] });
      getStore().commit(`${types.UPDATE_GEOFENCE_NELNG}`, { geofence: this, value: bbox[2] });
    }
  }

  updateCenter() {
    if (this.isPoint) {
      return;
    }
    const coordinates =
      this.geometry.type === 'MultiPolygon'
        ? this.geometry.coordinates.flat().flat()
        : this.geometry.coordinates.flat();
    const lats = coordinates.map((x) => x[1]);
    const lngs = coordinates.map((x) => x[0]);
    const minLat = lats.reduce((mem, x) => Math.min(x, mem), 90);
    const minLng = lngs.reduce((mem, x) => Math.min(x, mem), 180);
    const maxLat = lats.reduce((mem, x) => Math.max(x, mem), -90);
    const maxLng = lngs.reduce((mem, x) => Math.max(x, mem), -180);
    const lat = (minLat + maxLat) / 2;
    const lng = (minLng + maxLng) / 2;
    getStore().commit(`${types.UPDATE_GEOFENCE_LAT}`, { geofence: this, value: lat });
    getStore().commit(`${types.UPDATE_GEOFENCE_LNG}`, { geofence: this, value: lng });
  }

  static fromOSMApi(apiObject, usePolygons = true): Geofence {
    const displayNameFragments = apiObject.display_name.split(', ');
    const name = displayNameFragments[0];
    let description = '';
    if (displayNameFragments.length > 0) {
      const addressFragments = displayNameFragments.splice(1, displayNameFragments.length);
      description = addressFragments.join(', ');
    }

    const geoJson =
      apiObject.geojson != null && apiObject.geojson.type !== 'Point' && usePolygons
        ? JSON.stringify(apiObject.geojson)
        : null;

    return Geofence.fromObject({
      id: apiObject.place_id,
      boundsSwLng: Math.min(apiObject.boundingbox[2], apiObject.boundingbox[3]),
      boundsSwLat: Math.min(apiObject.boundingbox[0], apiObject.boundingbox[1]),
      boundsNeLng: Math.max(apiObject.boundingbox[2], apiObject.boundingbox[3]),
      boundsNeLat: Math.max(apiObject.boundingbox[0], apiObject.boundingbox[1]),
      lat: parseFloat(apiObject.lat),
      lng: parseFloat(apiObject.lon),
      name,
      description,
      geoJson,
      placeSearchJson: JSON.stringify(apiObject),
      placeSearchProvider: 'OSM',
      isPoint: geoJson == null,
      isSelected: true,
    });
  }

  static fromBaiduApi(apiObject): Geofence {
    let geoJson = null;
    if (apiObject.geoJson != null) {
      geoJson = apiObject.geoJson.geometry;
    }
    return Geofence.fromObject({
      id: apiObject.uid,
      boundsSwLng: apiObject.location.lng,
      boundsSwLat: apiObject.location.lat,
      boundsNeLng: apiObject.location.lng,
      boundsNeLat: apiObject.location.lat,
      lat: apiObject.location.lat,
      lng: apiObject.location.lng,
      name: apiObject.name,
      description: apiObject.name,
      geoJson,
      placeSearchJson: JSON.stringify(apiObject),
      placeSearchProvider: 'Here',
      isPoint: geoJson == null,
      isSelected: true,
    });
  }

  static fromGoogleApi(apiObject): Geofence {
    return Geofence.fromObject({
      id: apiObject.id || apiObject.place_id,
      boundsSwLng: apiObject.geometry.viewport.getSouthWest().lng(),
      boundsSwLat: apiObject.geometry.viewport.getSouthWest().lat(),
      boundsNeLng: apiObject.geometry.viewport.getNorthEast().lng(),
      boundsNeLat: apiObject.geometry.viewport.getNorthEast().lat(),
      lat: apiObject.geometry.location.lat(),
      lng: apiObject.geometry.location.lng(),
      name: apiObject.name,
      description: apiObject.formatted_address,
      geoJson: null,
      // placeSearchJson: JSON.stringify(apiObject),
      placeSearchProvider: 'Google',
      isPoint: true,
      isSelected: true,
    });
  }

  static fromHereApi(apiObject): Geofence {
    let geoJson = null;
    if (apiObject.geoJson != null) {
      geoJson = apiObject.geoJson.geometry;
    }
    return Geofence.fromObject({
      id: apiObject.id,
      boundsSwLng: apiObject.position[1],
      boundsSwLat: apiObject.position[0],
      boundsNeLng: apiObject.position[1],
      boundsNeLat: apiObject.position[0],
      lat: apiObject.position[0],
      lng: apiObject.position[1],
      name: apiObject.title,
      description: apiObject.vicinity.replace('<br/>', ', '),
      geoJson,
      placeSearchJson: JSON.stringify(apiObject),
      placeSearchProvider: 'Here',
      isPoint: geoJson == null,
      isSelected: true,
    });
  }

  static fromGenericGeofence(apiObject): Geofence {
    const geofence = Geofence.fromObject({
      id: apiObject.genericgeofence_id,
      name: apiObject.name,
      description: null,
      geoJson: apiObject.geometry,
      placeSearchProvider: 'GenericGeofence',
      isPoint: false,
      isSelected: true,
    });

    geofence.updateBoundingBox();
    geofence.updateCenter();
    return geofence;
  }

  static fromApi(apiObject): Geofence {
    const geofence = new Geofence(
      apiObject.id || apiObject.geofence_id,
      apiObject.ggid || apiObject.geofencegroup_id,
      apiObject.n || apiObject.name,
      apiObject.d || apiObject.description,
      apiObject.tg || apiObject.tags,
      apiObject.lat,
      apiObject.lng,
      apiObject.swt || apiObject.bounds_sw_lat,
      apiObject.swg || apiObject.bounds_sw_lng,
      apiObject.net || apiObject.bounds_ne_lat,
      apiObject.neg || apiObject.bounds_ne_lng,
      apiObject.g || apiObject.geojson,
      apiObject.search_results_json,
      apiObject.search_provider,
      apiObject.created_on_utc,
      apiObject.created_by,
      apiObject.modified_on_utc,
      apiObject.modified_by,
    );

    if (apiObject.dynamic_options != null || apiObject.dynamic_contour != null || apiObject.dynamic_profile != null) {
      geofence.dynamicGeofences = {
        params: {
          options: apiObject.dynamic_options,
          contour: apiObject.dynamic_contour,
          profile: apiObject.dynamic_profile,
          coordinates: [apiObject.lng, apiObject.lat],
          geofenceId: apiObject.id,
        },
        featureCollection: apiObject.g || apiObject.geojson,
      };
    }

    return geofence;
  }

  toApi(): IGeofenceAPI {
    // This is used create/update geofence.
    return {
      geofence_id: this.id >= 0 ? this.id : undefined,
      geofencegroup_id: this.geofenceGroupID,
      name: this.name,
      description: this.description,
      tags: this.tags,
      lat: this.lat,
      lng: this.lng,
      bounds_sw_lat: this.boundsSwLat,
      bounds_sw_lng: this.boundsSwLng,
      bounds_ne_lat: this.boundsNeLat,
      bounds_ne_lng: this.boundsNeLng,
      geojson: turfBuffer(this.geometry, 0).geometry,
      dynamic_options: this.dynamicGeofences?.params?.options || null,
      dynamic_contour: this.dynamicGeofences?.params?.contour || null,
      dynamic_profile: this.dynamicGeofences?.params?.profile || null,
    };
  }

  toShortenedApi(): IGeofenceShortenedAPI {
    return {
      id: this.id >= 0 ? this.id : undefined,
      ggid: this.geofenceGroupID,
      n: this.name,
      d: this.description,
      tg: this.tags,
      lat: this.lat,
      lng: this.lng,
      swt: this.boundsSwLat,
      swg: this.boundsSwLng,
      net: this.boundsNeLat,
      neg: this.boundsNeLng,
      g: this.isPoint ? null : turfBuffer(this.geometry, 0).geometry,
    };
  }

  toApiPreviewScreens(): IGeofenceForPreviewScreens {
    // This is used only to download. Following information is enough to fetch the data.
    return {
      geofence_id: this.id >= 0 ? this.id : undefined,
      geofencegroup_id: this.geofenceGroupID,
      name: this.name,
    };
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      id: 'required',
      geofenceGroupID: 'required',
      name: 'required',
      description: 'required',
      lat: 'required|numeric',
      lng: 'required|numeric',
    };
  }
}
